import { NestedStyles } from 'Shared/backend/styleUtils';
import { navStyles } from 'Shared/frontend/styles/navStyles';
import { textStyles } from 'Shared/frontend/styles/textStyles';
import { layoutStyles } from 'Shared/frontend/styles/layoutStyles';
import { transitionsStyles } from 'Shared/frontend/styles/transitionsStyles';
import { tweaksStyle } from 'Shared/frontend/styles/tweaksStyle';
import { overlayStyles } from 'Shared/frontend/styles/overlayStyles';
import { buttonStyles } from 'Shared/frontend/styles/buttonStyles';
import { footerStyles } from 'Shared/frontend/styles/footerStyles';
import { toolStyles } from 'Shared/frontend/styles/toolStyles';
import { specificPageStyles } from 'Shared/frontend/styles/specificPageStyles';
import { venueCustomStyles } from 'Shared/frontend/styles/venueCustomStyles';
import { settingRulesCss } from 'Shared/frontend/styles/settingRules';
import { ClassContext } from 'Shared/frontend/styles/ruleProperties';
import { CustomStyle, CustomStyleRule, NavSettings, Palette } from 'Shared/model/venueSettings';
import { StyleEntry } from 'Shared/backend/styleUtils';


/*
	The category the settings-page rule lists ride in - the three nav nodes, the footer and the event
	page. Not a style anybody turns on: it is one venue's settings wearing the shape of one, so that
	ShowWrapElement (nav, footer) and the event page's own style resolution can reach it the way they
	reach everything else. The style pickers leave it out by name; see StylesField.
*/
export const settingsRulesCategory = 'Settings rules';


/*
	The whole style catalogue.

	The code-defined categories are built first and then laid back out in the order the style pickers
	show them in, with the venue's own styles in their place among them - after the rest, before
	Tweaks.

	'palette' is the venue's, and is handed down rather than read off the theme context: this runs
	while siteTheme() is still being assembled, so there is nothing to read it from yet.
*/
export function standardStyles(sizes,text,venueCustomStyleList:CustomStyle[],
	nav:NavSettings, footerRules:CustomStyleRule[], eventRules:CustomStyleRule[],
	palette:Palette): NestedStyles
{
	const shared: NestedStyles = {
		layout: layoutStyles(sizes,text),
		text: textStyles(sizes,text),
		transitions: transitionsStyles(sizes,text),

		/* By component: */
		overlays: overlayStyles(sizes,text),
		buttons: buttonStyles(),

		/* By page: */
		'For specific pages': specificPageStyles(sizes,text),

		/* Headers, footers: */
		nav: navStyles(sizes,text),
		footers: footerStyles(sizes,text),

		tools: toolStyles(sizes,text),

		tweaks: tweaksStyle(sizes,text),
	};

	const {tweaks, ...aboveVenueStyles} = shared;

	return {
		...aboveVenueStyles,

		/* The venue's own styles, all built in the Style Builder now and stored on the venue document. */
		'Venue styles': venueCustomStyles(venueCustomStyleList,palette),

		[settingsRulesCategory]: {
			desktop: settingsNodeEntry(nav.desktop.rules,'navDesktop',['nav','desktop'],palette),
			mobile:  settingsNodeEntry(nav.mobile.rules,'navMobile',['nav','mobile'],palette),
			more:    settingsNodeEntry(nav.mobile.more.rules,'navMore',['nav','more'],palette),
			footer:  settingsNodeEntry(footerRules,'footer',['footer'],palette),
			/* The event page has no wrapper - the rule's location lands it straight on the date and
			   name <h3>s, and misses the lineup artist names below (which carry the same class). */
			event:   settingsNodeEntry(eventRules,'event',['^','*:title'],palette),
		},

		tweaks,
	};
}


/*
	One settings rule list as a single entry at the location its wrapper (or, for the event page, its
	target element) sits at - ['nav','desktop'] and the like, the paths ShowWrapElement resolves and
	navStyles.ts / footerStyles.ts's own rules already sit at.

	One rule holding one css object, not a rule per stored rule: applyRule() shallow-merges the css of
	everything that applies, so two rules sharing a part's selector key would clobber each other.
	settingRulesCss() does that merge properly.
*/
function settingsNodeEntry(rules:CustomStyleRule[], context:ClassContext, location:string[],
	palette:Palette): StyleEntry
{
	return {
		rules: [{
			location: () => location,
			css:      () => settingRulesCss(rules,context,palette),
		}],
	};
}

